import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, Trash2, X, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BOOK } from "../content";
import { useCart } from "../context/CartContext";
import BookCover from "./BookCover";

const CartDrawer = () => {
  const { inCart, drawerOpen, setDrawerOpen, removeFromCart } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full max-w-md bg-[#06333E] border-l border-[#D4AF37]/20 flex flex-col"
            role="dialog"
            aria-label="Shopping cart"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#D4AF37]/15">
              <h2 className="font-display text-lg font-bold tracking-widest text-[#F8FAFC]">YOUR CART</h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-full text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#D4AF37]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {inCart ? (
                <div className="flex gap-5 rounded-lg border border-[#D4AF37]/15 bg-[#0A404E] p-4" data-testid="cart-item">
                  <div className="w-20 shrink-0 pointer-events-none">
                    <BookCover />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-base font-semibold text-[#F8FAFC]">{BOOK.title}</h3>
                    <p className="mt-0.5 text-[11px] text-[#94A3B8]">{BOOK.subtitle}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-2.5 py-1 text-[10px] font-mono-alt uppercase tracking-wider text-[#FCD34D]">
                        <Zap className="w-3 h-3" /> Instant PDF
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-2.5 py-1 text-[10px] font-mono-alt uppercase tracking-wider text-[#FCD34D]">
                        + Email link
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-xl font-bold text-[#F59E0B]" data-testid="cart-item-price">${BOOK.price.toFixed(2)}</span>
                        <span className="text-sm text-[#94A3B8] line-through">${BOOK.originalPrice.toFixed(2)}</span>
                      </div>
                      <button
                        data-testid="cart-remove-button"
                        onClick={removeFromCart}
                        className="p-2 text-[#94A3B8] hover:text-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37] rounded"
                        aria-label="Remove from cart"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4" data-testid="cart-empty">
                  <p className="font-serif-alt italic text-xl text-[#94A3B8]">The shelf is empty.</p>
                  <button
                    data-testid="cart-empty-close-button"
                    onClick={() => setDrawerOpen(false)}
                    className="text-sm text-[#FCD34D] underline underline-offset-4 hover:text-[#D4AF37]"
                  >
                    Return to the Archive
                  </button>
                </div>
              )}
            </div>

            {inCart && (
              <div className="border-t border-[#D4AF37]/15 px-6 py-6 space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-[#94A3B8]">
                    <span>Original price</span>
                    <span className="line-through">${BOOK.originalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#94A3B8]">
                    <span>Early-reader discount</span>
                    <span className="text-[#D4AF37]">−${(BOOK.originalPrice - BOOK.price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold text-[#F8FAFC] pt-2 border-t border-[#D4AF37]/15">
                    <span>Total</span>
                    <span data-testid="cart-total">${BOOK.price.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={goCheckout}
                  className="w-full rounded-full bg-[#D4AF37] py-4 font-semibold text-[#052730] transition-all duration-300 hover:bg-[#FCD34D] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] focus:outline-none focus:ring-2 focus:ring-[#FCD34D] active:scale-[0.98]"
                >
                  Proceed to Checkout
                </button>
                <p className="flex items-center justify-center gap-2 text-[11px] text-[#94A3B8]">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Guaranteed instant delivery after payment
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
